import { Modal, message, Layout } from 'antd'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import {
  Delete,
  IconCelaLeft,
  IconCelaRight,
  IconComment,
} from 'wujieai-react-icon'
import { MyBtn } from '~/components/ui/MyBtn'
import dayjs from 'dayjs'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styles from './ChatHistory.module.css'
import {
  ChatMessageStatusEnum,
  ConversationType,
  getChatMessage,
  getChatMessageVariables,
  getConversationList,
  getConversationListVariables,
  getConversationList_openAiQuery_pageConversation_edges_node,
} from '~/graphqls/types'
import { getGraphData, graphQLErrorMessage } from '~/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  chatMessagesAtom,
  conversationIdAtom,
  isUpdateHistoryAtom,
  searchResultsAtom,
  selectMessageAtom,
  curHistoryConversationAtom,
  pollConversationIdsAtom,
} from './atom'
import { GET_CHAT_MESSAGE, GET_CONVERSATION_LIST } from '~/graphqls/gqls/gpt'
import HistoryActions from './HistoryActions'

const { Sider } = Layout
interface IResult {
  today: getConversationList_openAiQuery_pageConversation_edges_node[]
  yesterday: getConversationList_openAiQuery_pageConversation_edges_node[]
  withinWeek: getConversationList_openAiQuery_pageConversation_edges_node[]
  withinMonth: getConversationList_openAiQuery_pageConversation_edges_node[]
  beyondMonth: getConversationList_openAiQuery_pageConversation_edges_node[]
}

const ChatHistory: React.FC<{
  stopMessage: () => void
  conversationType: ConversationType
}> = ({ stopMessage, conversationType }) => {
  const [isUpdateHistory, setIsUpdateHistory] = useAtom(isUpdateHistoryAtom)
  const messages = useAtomValue(selectMessageAtom)
  const setMessages = useSetAtom(chatMessagesAtom)
  const setConversationId = useSetAtom(conversationIdAtom)
  const setSearchResult = useSetAtom(searchResultsAtom)
  const [pollConversationIds, setPollConversationIds] = useAtom(
    pollConversationIdsAtom
  )
  const [data, setData] = useState<
    getConversationList_openAiQuery_pageConversation_edges_node[]
  >([])
  const [collapsed, setCollapsed] = useState(true)
  const [editItem, setEditItem] = useState<
    getConversationList_openAiQuery_pageConversation_edges_node | undefined
  >(undefined)
  const setCurHistoryConversation = useSetAtom(curHistoryConversationAtom)

  const getHistoryList = useImperativeQuery<
    getConversationList,
    getConversationListVariables
  >(GET_CONVERSATION_LIST)

  const getChatMessageApi = useImperativeQuery<
    getChatMessage,
    getChatMessageVariables
  >(GET_CHAT_MESSAGE)

  //  第一条会话信息
  const firstConversationId = useMemo(() => {
    return messages.filter(it => it.parentId === 0)?.[0]?.conversationId
  }, [messages])

  const fetchList = async () => {
    try {
      const res = await getHistoryList({
        first: 100,
        offset: 0,
        param: {
          conversationType,
        },
      })
      const list = getGraphData(
        res.data.openAiQuery?.pageConversation?.edges || []
      )
      // if (!list.length) {
      //   setMessages([])
      //   setConversationId(null)
      // }
      setData(list)
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || '网络错误，请稍后重试')
    }
  }

  const handleSelectConversation = async (
    item?: getConversationList_openAiQuery_pageConversation_edges_node
  ) => {
    try {
      const res = await getChatMessageApi({ id: item?.id })
      setSearchResult(null)
      setConversationId(item?.currentMessage?.conversationId)
      setCurHistoryConversation(item)

      const chatMessageList = res.data.openAiQuery?.chatMessage

      if (conversationType === ConversationType.GPT) {
        setMessages(prev => {
          return {
            [item?.currentMessage?.conversationId!]: {
              messages:
                chatMessageList?.map(it => ({
                  id: it?.id,
                  role: it?.role!,
                  content: it?.content?.content,
                  image: it?.content?.image,
                  referImages: it?.content?.referImages,
                  conversationId: it?.conversationId,
                  parentId: it?.parentId,
                  status: it?.status,
                })) || [],
            },
            ...prev,
          }
        })
        // 未轮询结束的，继续轮询
        if (
          chatMessageList?.[chatMessageList.length - 1]?.status ===
            ChatMessageStatusEnum.INIT &&
          !pollConversationIds?.includes(item?.currentMessage?.id!)
        ) {
          setPollConversationIds(prev => [...prev, item?.currentMessage?.id!])
        }
      } else {
        setMessages(prev => {
          return {
            [item?.currentMessage?.conversationId!]: {
              messages:
                chatMessageList?.map(it => ({
                  id: it?.id,
                  role: it?.role!,
                  content: it?.content?.content!,
                  reasoning: it?.thinkingContent?.content!,
                  thinkingTime: it?.thinkingTime,
                  conversationId: it?.conversationId,
                  parentId: it?.parentId,
                  thinkingEnabled: it?.thinkingEnabled || false,
                  done: true,
                  searchResults: it?.searchContents,
                })) || [],
            },
            ...prev,
          }
        })
      }
    } catch (error) {
      message.destroy()
      message.error(graphQLErrorMessage(error) || '网络错误，请稍后重试')
    }
  }

  const classifyByTimestamp = (
    list: getConversationList_openAiQuery_pageConversation_edges_node[]
  ) => {
    const now = dayjs()
    const result: IResult = {
      today: [],
      yesterday: [],
      withinWeek: [],
      withinMonth: [],
      beyondMonth: [],
    }
    list.forEach(it => {
      const date = dayjs((it.updateTime || it.createTime) * 1000)
      // 今天
      if (date.isSame(now, 'day')) {
        result.today.push(it)
      }
      // 昨天
      else if (date.isSame(now.subtract(1, 'day'), 'day')) {
        result.yesterday.push(it)
      }
      // 7天内
      else if (date.isAfter(now.subtract(7, 'day'))) {
        result.withinWeek.push(it)
      }
      // 30天内
      else if (date.isAfter(now.subtract(30, 'day'))) {
        result.withinMonth.push(it)
      }
      // 30天以外
      else {
        result.beyondMonth.push(it)
      }
    })

    return result
  }

  const { today, beyondMonth, yesterday, withinWeek, withinMonth } =
    useMemo(() => {
      return classifyByTimestamp(data)
    }, [data])

  useEffect(() => {
    fetchList()
  }, [])

  useEffect(() => {
    if (isUpdateHistory) {
      setTimeout(() => {
        fetchList()
      }, 500)
    }
  }, [isUpdateHistory])

  const list = [
    {
      key: '1',
      time: '今天',
      items: today,
    },
    {
      key: '2',
      time: '昨天',
      items: yesterday,
    },
    {
      key: '3',
      time: '7 天内',
      items: withinWeek,
    },
    {
      key: '4',
      time: '30 天内',
      items: withinMonth,
    },
    {
      key: '5',
      time: '30 天外',
      items: beyondMonth,
    },
  ]
  return (
    <Sider
      trigger={null}
      collapsible
      width={290}
      collapsedWidth={64}
      collapsed={collapsed}
      className={styles.sider}
    >
      <div
        className={classNames(
          'sticky top-0 z-10 bg-neutral-100 flex  px-3 pt-3 cursor-pointer',
          collapsed ? 'justify-center' : 'justify-end'
        )}
        onClick={() => {
          setCollapsed(val => {
            if (val) {
              fetchList()
            }
            return !val
          })
        }}
      >
        {collapsed ? (
          <IconCelaRight size={24} className="text-neutral-400" />
        ) : (
          <IconCelaLeft size={24} className="text-neutral-400" />
        )}
      </div>
      <div
        className={classNames(
          'flex-1 pb-[80px] px-3  h-full overflow-auto scrollbar-hidden',
          collapsed && 'opacity-0'
        )}
      >
        {list.map(it => (
          <div
            key={it.key}
            className={!it.items?.length ? 'hidden' : 'visible'}
          >
            <div className="text-neutral-500 font-semibold text-xs mb-1">
              {it.time}
            </div>
            {it.items.map(ele =>
              ele.id !== editItem?.id ? (
                <div
                  key={ele.id}
                  onClick={() => {
                    if (collapsed) return
                    handleSelectConversation(ele)
                  }}
                >
                  <div
                    className={classNames(
                      'flex items-center p-2 text-neutral-500 rounded-[8px] cursor-pointer',
                      {
                        'bg-[#34226A] !text-primary':
                          ele.currentMessage?.conversationId ===
                          firstConversationId,
                      }
                    )}
                  >
                    <div className="line-clamp-1 text-sm font-semibold  flex-1">
                      {ele.title}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <HistoryActions
                        collapsed={collapsed}
                        item={ele}
                        onDeleteOk={item => {
                          if (
                            item?.currentMessage?.conversationId ===
                            firstConversationId
                          ) {
                            setMessages(prev => {
                              return {
                                ...prev,
                                [item?.currentMessage?.conversationId!]: {
                                  messages: [],
                                },
                              }
                            })
                            setSearchResult(null)
                            setConversationId(null)
                            setCurHistoryConversation(undefined)
                            setIsUpdateHistory(false)
                            stopMessage()
                          }
                          fetchList()
                        }}
                        onEdit={item => {
                          setEditItem(item)
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div key={ele.id}>
                  <HistoryActions.Editor
                    editItem={editItem}
                    setEditItem={setEditItem}
                    onEditOk={item => {
                      // 更新title
                      setCurHistoryConversation(pre => {
                        return pre?.id === item?.id ? { ...item! } : pre
                      })
                      // 更新列表
                      setData([...data])
                    }}
                  />
                </div>
              )
            )}
          </div>
        ))}
      </div>
      <div
        className={classNames(
          styles.btn,
          'absolute bottom-0 left-0   px-3 py-6'
        )}
        style={{
          background:
            'linear-gradient(0deg, #141416 72.16%, rgba(20, 20, 22, 0.00) 100%)',
        }}
      >
        <MyBtn
          type="primary"
          size="large"
          className="w-full !h-10 !p-0 transition-all duration-200"
          onClick={() => {
            setIsUpdateHistory(false)
            setSearchResult(null)
            setConversationId(null)

            setMessages(prev => {
              return {
                ...prev,
                default: {
                  messages: [],
                },
              }
            })
          }}
        >
          <div className="flex justify-center items-center text-sm">
            <IconComment
              size={20}
              className="ml-[3.5px] text-neutral-800 transition-transform duration-200"
            />
            <span
              className={classNames(
                'ml-1 transition-all duration-200 overflow-hidden',
                {
                  'w-0 opacity-0': collapsed,
                  'w-auto opacity-100': !collapsed,
                }
              )}
            >
              开启新对话
            </span>
          </div>
        </MyBtn>
      </div>
    </Sider>
  )
}
export default ChatHistory
